import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Stack, Typography } from "@mui/material";
import { FC, memo, useMemo } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import { CollapsibleCard } from "@/components/elements/CollapsibleCard";
import { Switch } from "@/modules/irohana-components/elements/Switch";
import { FormField } from "@/modules/irohana-components/elements/FormField";
import { Table } from "@/modules/irohana-components/elements/Table";
import { useTable } from "@/modules/irohana-components/hooks/useTable";
import { text } from "@/theme/colors";
import {
  ADD_ROW_LABEL,
  PRODUCT_FORM_TABLE_MAX_HEIGHT,
} from "@/features/organization/product/constants/productEnums";
import { CombinationSummaryBar } from "@/features/organization/product/components/product-form/product-combination/combinationCells";
import { TProductForm } from "@/api/services/main/organization/product/dtos/product.dto";
import { emptyCombinationForm } from "@/features/organization/product/utils/productForm";
import { useProductCombinationSection } from "@/features/organization/product/hooks/useProductCombinationSection";
import { useProductMasters } from "@/features/organization/product/hooks/useProductMasters";

type TProductCombinationSectionProps = { isTierDiscountTarget: boolean };

/** 単価組み合わせ条件設定 — toggles + summary bar + editable table (登録・編集). */
export const ProductCombinationSection: FC<TProductCombinationSectionProps> = memo(({ isTierDiscountTarget }) => {
  const { control } = useFormContext<TProductForm>();
  const { getNationalityName, getIndustryName } = useProductMasters();
  const productName = useWatch({ control, name: "name" }) ?? "";
  const typePrices = useWatch({ control, name: "typePrices" }) ?? [];
  const nationalityEnabled = useWatch({ control, name: "nationalityEnabled" });
  const fieldEnabled = useWatch({ control, name: "fieldEnabled" });
  const variantEnabled = useWatch({ control, name: "variantEnabled" });
  const { fields, append, remove, move } = useFieldArray({ control, name: "combinations" });

  const {
    showThirdDimension,
    thirdDimensionHeader,
    columns,
  } = useProductCombinationSection({
    control,
    productName,
    isTierDiscountTarget,
    typePrices,
    rows: fields,
    nationalityEnabled,
    fieldEnabled,
    variantEnabled,
    append,
    remove,
    getNationalityName,
    getIndustryName,
  });

  const { table } = useTable({
    rows: fields,
    columns,
    getRowId: (r) => r.id,
    pinningColumns: ["action"],
    // Drag-to-reorder — persist the new order into the field array (row position = 表示順).
    enableRowOrdering: true,
    // Hide the default "Move" header text on the drag-handle column.
    displayColumnDefOptions: { "mrt-row-drag": { header: "" } },
    // Static ⋮⋮ grip handle (grab on hover); drag the handle to reorder.
    icons: { DragHandleIcon: () => <DragIndicatorIcon fontSize="small" sx={{ color: text.tertiary }} /> },
    muiRowDragHandleProps: ({ table: t }) => ({
      sx: { cursor: "grab" },
      onDragEnd: () => {
        const from = t.getState().draggingRow?.index;
        const to = t.getState().hoveredRow?.index;
        if (from != null && to != null) move(from, to);
      },
    }),
  });
  const enhancedTable = useMemo(() => ({ ...table, addNewRow: () => append(emptyCombinationForm()) }), [table, append]);

  return (
    <CollapsibleCard
      title={<Typography variant="body14Bold" color={text.primary}>単価組み合わせ条件設定</Typography>}
      description={
        <>
          国籍・分野・ティアを組み合わせた単価を設定できます。条件は個別にON/OFFで切り替えられます。
          <br />
          ONにした条件のみ絞り込みに使用されます。OFFの条件は「全て」として扱われます。
        </>
      }
      showBorder={false}
      sx={{ pt: 0 }}
    >
      <Stack gap={2}>
        <Stack direction="row" gap={3} alignItems="center" flexWrap="wrap">
          <FormField control={control} name="nationalityEnabled" noLabel sx={{ width: "auto", "& .MuiFormControlLabel-root": { ml: 0 } }}><Switch label="国籍" /></FormField>
          <FormField control={control} name="fieldEnabled" noLabel sx={{ width: "auto", "& .MuiFormControlLabel-root": { ml: 0 } }}><Switch label="分野" /></FormField>
          {showThirdDimension && <FormField control={control} name="variantEnabled" noLabel sx={{ width: "auto", "& .MuiFormControlLabel-root": { ml: 0 } }}><Switch label={thirdDimensionHeader} /></FormField>}
        </Stack>

        <CombinationSummaryBar control={control} />

        <Table
          table={enhancedTable}
          addBtnText={ADD_ROW_LABEL}
          maxHeight={PRODUCT_FORM_TABLE_MAX_HEIGHT}
        />
      </Stack>
    </CollapsibleCard>
  );
});

ProductCombinationSection.displayName = "ProductCombinationSection";
